/**
 * Tasks Service
 * Handles Firestore operations for user tasks
 * Path: /users/{uid}/tasks/{taskId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
  writeBatch,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { logger } from '@/lib/logger';
import { db } from '@/lib/firebase';
import { Task, TaskInput, TaskQueryOptions, TaskStatus, TaskScope } from '@/types/notepad';
import { toTaskWriteModel } from '@/features/tasks/task.mapper';
import type { TaskWriteModelOptions } from '@/features/tasks/task.mapper';

/**
 * Get tasks collection reference for a user (personal tasks)
 */
function getTasksCollection(userId: string) {
  return collection(db, 'users', userId, 'tasks');
}

/**
 * Get tasks collection reference for a team (team tasks)
 */
function getTeamTasksCollection(teamId: string) {
  return collection(db, 'teams', teamId, 'tasks');
}

/**
 * Convert Firestore document to Task
 * @param isTeamTask - If true, sets scope to 'team', otherwise 'personal'
 */
function docToTask(docSnap: DocumentSnapshot<DocumentData>, isTeamTask: boolean = false): Task {
  const data = docSnap.data();
  if (!data) {
    throw new Error('Document data is undefined');
  }
  return {
    id: docSnap.id,
    ownerId: data.ownerId,
    teamId: data.teamId,
    // CRITICAL: scope determines which Firestore path to use for mutations
    scope: data.scope || (isTeamTask ? 'team' : 'personal'),
    title: data.title,
    noteId: data.noteId,
    status: data.status,
    priority: data.priority,
    tags: data.tags || [],
    dueAt: data.dueAt,
    remindAt: data.remindAt,
    repeat: data.repeat,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    completedAt: data.completedAt,
    deletedAt: data.deletedAt,
  };
}

/**
 * List personal tasks with optional filters
 */
export async function listTasks(userId: string, options: TaskQueryOptions = {}): Promise<Task[]> {
  try {
    const tasksRef = getTasksCollection(userId);
    const constraints: QueryConstraint[] = [];

    // Exclude soft-deleted tasks
    constraints.push(where('deletedAt', '==', null));

    // Filter by status
    if (options.statuses && options.statuses.length > 0) {
      constraints.push(where('status', 'in', options.statuses));
    }

    // Filter by team context
    if (options.teamId) {
      constraints.push(where('teamId', '==', options.teamId));
    }

    // Filter by time window
    if (options.windowStart) {
      constraints.push(where('dueAt', '>=', Timestamp.fromDate(options.windowStart)));
    }
    if (options.windowEnd) {
      constraints.push(where('dueAt', '<=', Timestamp.fromDate(options.windowEnd)));
    }

    // Order by status and dueAt (uses composite index)
    if (options.statuses && options.statuses.length > 0) {
      constraints.push(orderBy('status', 'asc'));
    }
    constraints.push(orderBy('dueAt', 'desc'));

    // Apply limit
    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(tasksRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => docToTask(doc, false));
  } catch (error) {
    logger.error('[TasksService] Failed to list tasks:', error);
    throw error;
  }
}

/**
 * List team tasks with optional filters
 */
export async function listTeamTasks(
  teamId: string,
  options: TaskQueryOptions = {}
): Promise<Task[]> {
  try {
    const tasksRef = getTeamTasksCollection(teamId);
    const constraints: QueryConstraint[] = [];

    // Exclude soft-deleted tasks
    constraints.push(where('deletedAt', '==', null));

    // Filter by status
    if (options.statuses && options.statuses.length > 0) {
      constraints.push(where('status', 'in', options.statuses));
    }

    // Filter by time window
    if (options.windowStart) {
      constraints.push(where('dueAt', '>=', Timestamp.fromDate(options.windowStart)));
    }
    if (options.windowEnd) {
      constraints.push(where('dueAt', '<=', Timestamp.fromDate(options.windowEnd)));
    }

    // Order by dueAt
    constraints.push(orderBy('dueAt', 'desc'));

    // Apply limit
    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(tasksRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => docToTask(doc, true)); // true = team task
  } catch (error) {
    logger.error('[TasksService] Failed to list team tasks:', error);
    throw error;
  }
}

/**
 * List ALL tasks (personal + team) merged together
 * Each task has correct scope set for subsequent mutations
 */
export async function listAllTasks(
  userId: string,
  teamId: string,
  options: TaskQueryOptions = {}
): Promise<Task[]> {
  try {
    // Fetch both personal and team tasks in parallel
    const [personalTasks, teamTasks] = await Promise.all([
      listTasks(userId, options),
      listTeamTasks(teamId, options),
    ]);

    // Merge and sort by dueAt (most recent first)
    const allTasks = [...personalTasks, ...teamTasks];
    allTasks.sort((a, b) => {
      const aDate = a.dueAt?.toDate()?.getTime() || 0;
      const bDate = b.dueAt?.toDate()?.getTime() || 0;
      return bDate - aDate; // descending
    });

    return allTasks;
  } catch (error) {
    logger.error('[TasksService] Failed to list all tasks:', error);
    throw error;
  }
}

/**
 * Get a single task by ID
 * Checks both personal tasks (/users/{uid}/tasks) and team tasks (/teams/{teamId}/tasks)
 * Returns task with correct scope set for subsequent mutations
 */
export async function getTask(
  userId: string,
  taskId: string,
  teamId?: string
): Promise<Task | null> {
  try {
    // First try personal tasks
    const personalTaskRef = doc(db, 'users', userId, 'tasks', taskId);
    const personalTaskSnap = await getDoc(personalTaskRef);

    if (personalTaskSnap.exists()) {
      const task = docToTask(personalTaskSnap, false); // personal task
      // Don't return soft-deleted tasks
      if (!task.deletedAt) {
        return task;
      }
    }

    // If not found in personal tasks and teamId is provided, try team tasks
    if (teamId) {
      const teamTaskRef = doc(db, 'teams', teamId, 'tasks', taskId);
      const teamTaskSnap = await getDoc(teamTaskRef);

      if (teamTaskSnap.exists()) {
        const task = docToTask(teamTaskSnap, true); // team task
        // Don't return soft-deleted tasks
        if (!task.deletedAt) {
          return task;
        }
      }
    }

    return null;
  } catch (error) {
    logger.error('[TasksService] Failed to get task:', error);
    throw error;
  }
}

/**
 * Create a new task
 * Supports both personal tasks (/users/{uid}/tasks) and team tasks (/teams/{teamId}/tasks)
 * Uses canonical mapper for normalization, defaults, and provenance.
 */
export async function createTask(
  userId: string,
  input: TaskInput,
  options?: { mapperOptions?: TaskWriteModelOptions }
): Promise<Task> {
  try {
    // Canonical mapper — normalization, defaults, provenance (MAP-1..5, PROV-1..5)
    const mapped = toTaskWriteModel(input as unknown as Record<string, unknown>, {
      mode: 'create',
      ...options?.mapperOptions,
    });

    const scope = (mapped.scope as string) || 'personal';
    const isTeamTask = scope === 'team';

    // Guard: team-scoped tasks require a non-empty teamId — fail fast before Firestore
    if (
      isTeamTask &&
      (!input.teamId || typeof input.teamId !== 'string' || input.teamId.trim() === '')
    ) {
      logger.error('[TasksService] createTask: scope=team but teamId is missing or empty', {
        input,
      });
      throw new Error(
        '[TasksService] teamId is required and must be a non-empty string for team-scoped tasks'
      );
    }

    // Choose the correct collection based on scope
    const tasksRef = isTeamTask
      ? getTeamTasksCollection(input.teamId!)
      : getTasksCollection(userId);

    // Normalize status to allowed values for team tasks
    // Firestore rules only allow: 'open', 'done', 'dismissed'
    const normalizedStatus =
      mapped.status === 'in_progress' || mapped.status === 'blocked'
        ? 'open'
        : (mapped.status as string) || 'open';

    const taskData = {
      ...mapped,
      ownerId: userId,
      createdBy: userId, // Required by Firestore rules for team tasks
      teamId: input.teamId || null,
      status: normalizedStatus,
      dueAt: input.dueAt ? Timestamp.fromDate(input.dueAt) : null,
      remindAt: input.remindAt ? Timestamp.fromDate(input.remindAt) : null,
      repeat: input.repeat || null,
      scope, // Store scope for clarity
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      completedAt: null,
      deletedAt: null,
    };

    const docRef = await addDoc(tasksRef, taskData);

    // For team tasks, we need to fetch from the team collection
    if (isTeamTask) {
      const taskSnap = await getDoc(docRef);
      if (!taskSnap.exists()) {
        throw new Error('Failed to retrieve created team task');
      }
      return docToTask(taskSnap);
    }

    // For personal tasks, use existing getTask
    const newTask = await getTask(userId, docRef.id);

    if (!newTask) {
      throw new Error('Failed to retrieve created task');
    }

    return newTask;
  } catch (error) {
    logger.error('[TasksService] Failed to create task:', error);
    throw error;
  }
}

/**
 * Update an existing task
 * Routes to correct Firestore path based on task scope
 * Strips create-only fields (createdVia, createdViaRefId) per SVC-5 / PROV-2.
 *
 * @param userId - Current user ID
 * @param taskId - Task ID to update
 * @param input - Fields to update
 * @param teamId - Team ID (required for team tasks)
 */
export async function updateTask(
  userId: string,
  taskId: string,
  input: Partial<TaskInput>,
  teamId?: string
): Promise<Task> {
  try {
    // First fetch the task to determine its scope
    const existingTask = await getTask(userId, taskId, teamId);
    if (!existingTask) {
      throw new Error('Task not found');
    }

    // Determine if this is a team task based on scope
    const isTeamTask = existingTask.scope === 'team';

    // Get the correct document reference based on scope
    const taskRef =
      isTeamTask && teamId
        ? doc(db, 'teams', teamId, 'tasks', taskId)
        : doc(db, 'users', userId, 'tasks', taskId);

    // Strip create-only / immutable fields (PROV-2, SVC-5)
    const rawPatch = input as Record<string, unknown>;
    const {
      createdVia: _cv,
      createdViaRefId: _cvr,
      createdBy: _cb,
      createdAt: _ca,
      ownerId: _oid,
      ...safePatch
    } = rawPatch;

    // Canonical mapper in update mode (no defaults, no provenance)
    const mapped = toTaskWriteModel(safePatch, { mode: 'update' });

    const updates: Record<string, unknown> = {
      ...mapped,
      updatedAt: serverTimestamp(),
    };

    if (input.dueAt !== undefined) {
      updates.dueAt = input.dueAt ? Timestamp.fromDate(input.dueAt) : null;
    }
    if (input.remindAt !== undefined) {
      updates.remindAt = input.remindAt ? Timestamp.fromDate(input.remindAt) : null;
    }

    // Handle completedAt based on status change
    if (input.status === 'done') {
      updates.completedAt = serverTimestamp();
    } else if (input.status && existingTask.status === 'done') {
      // If changing from done to any other status, clear completedAt
      updates.completedAt = null;
    }

    await updateDoc(taskRef, updates);

    const updatedTask = await getTask(userId, taskId, teamId);
    if (!updatedTask) {
      throw new Error('Failed to retrieve updated task');
    }

    logger.info('[TasksService] Updated task:', { taskId, isTeamTask, scope: existingTask.scope });
    return updatedTask;
  } catch (error) {
    logger.error('[TasksService] Failed to update task:', error);
    throw error;
  }
}

/**
 * Toggle task done status
 * Handles both personal tasks (/users/{uid}/tasks) and team tasks (/teams/{teamId}/tasks)
 *
 * @param userId - Current user ID
 * @param taskId - Task ID to toggle
 * @param teamId - Optional team ID (required for team tasks)
 */
export async function toggleTaskDone(
  userId: string,
  taskId: string,
  teamId?: string
): Promise<Task> {
  try {
    const task = await getTask(userId, taskId, teamId);
    if (!task) {
      throw new Error('Task not found');
    }

    const newStatus: TaskStatus = task.status === 'done' ? 'open' : 'done';
    const updates: Record<string, unknown> = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };

    if (newStatus === 'done') {
      updates.completedAt = serverTimestamp();
    } else {
      updates.completedAt = null;
    }

    // Determine if this is a team task based on scope or where it was found
    // Team tasks have scope: 'team' or are stored in /teams/{teamId}/tasks
    const isTeamTask = task.scope === 'team' || (task.teamId && task.teamId === teamId);

    // Get the correct document reference
    const taskRef =
      isTeamTask && teamId
        ? doc(db, 'teams', teamId, 'tasks', taskId)
        : doc(db, 'users', userId, 'tasks', taskId);

    await updateDoc(taskRef, updates);

    const updatedTask = await getTask(userId, taskId, teamId);
    if (!updatedTask) {
      throw new Error('Failed to retrieve updated task');
    }

    logger.info('[TasksService] Toggled task done:', { taskId, isTeamTask, newStatus });
    return updatedTask;
  } catch (error) {
    logger.error('[TasksService] Failed to toggle task done:', error);
    throw error;
  }
}

/**
 * Snooze a task by adding minutes to dueAt
 * Routes to correct Firestore path based on task scope
 */
export async function snoozeTask(
  userId: string,
  taskId: string,
  minutes: number,
  teamId?: string
): Promise<Task> {
  try {
    const task = await getTask(userId, taskId, teamId);
    if (!task) {
      throw new Error('Task not found');
    }

    const currentDue = task.dueAt?.toDate() || new Date();
    const newDue = new Date(currentDue.getTime() + minutes * 60 * 1000);

    // Route based on scope
    const isTeamTask = task.scope === 'team';
    const taskRef =
      isTeamTask && teamId
        ? doc(db, 'teams', teamId, 'tasks', taskId)
        : doc(db, 'users', userId, 'tasks', taskId);

    await updateDoc(taskRef, {
      dueAt: Timestamp.fromDate(newDue),
      updatedAt: serverTimestamp(),
    });

    const updatedTask = await getTask(userId, taskId, teamId);
    if (!updatedTask) {
      throw new Error('Failed to retrieve updated task');
    }

    return updatedTask;
  } catch (error) {
    logger.error('[TasksService] Failed to snooze task:', error);
    throw error;
  }
}

/**
 * Snooze a task from dashboard (handles both team and personal tasks)
 * Task IDs from dashboard are prefixed: "team-{id}" or "personal-{id}"
 * Snooze sets dueAt to tomorrow (start of day + 9am)
 */
export async function snoozeDashboardTask(
  userId: string,
  teamId: string,
  prefixedTaskId: string
): Promise<void> {
  try {
    // Parse the prefixed ID
    const isTeamTask = prefixedTaskId.startsWith('team-');
    const isPersonalTask = prefixedTaskId.startsWith('personal-');

    if (!isTeamTask && !isPersonalTask) {
      throw new Error(`Invalid task ID format: ${prefixedTaskId}`);
    }

    const realTaskId = isTeamTask
      ? prefixedTaskId.replace('team-', '')
      : prefixedTaskId.replace('personal-', '');

    // Calculate tomorrow 9am
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);

    // Get the correct document reference
    const taskRef = isTeamTask
      ? doc(db, 'teams', teamId, 'tasks', realTaskId)
      : doc(db, 'users', userId, 'tasks', realTaskId);

    await updateDoc(taskRef, {
      dueAt: Timestamp.fromDate(tomorrow),
      updatedAt: serverTimestamp(),
    });

    logger.info('[TasksService] Snoozed dashboard task:', {
      prefixedTaskId,
      realTaskId,
      isTeamTask,
      newDueAt: tomorrow,
    });
  } catch (error) {
    logger.error('[TasksService] Failed to snooze dashboard task:', error);
    throw error;
  }
}

/**
 * Archive a task from dashboard (handles both team and personal tasks)
 * Task IDs from dashboard are prefixed: "team-{id}" or "personal-{id}"
 * Archive sets status to 'dismissed' - removes from dashboards for everyone
 */
export async function archiveDashboardTask(
  userId: string,
  teamId: string,
  prefixedTaskId: string
): Promise<void> {
  try {
    // Parse the prefixed ID
    const isTeamTask = prefixedTaskId.startsWith('team-');
    const isPersonalTask = prefixedTaskId.startsWith('personal-');

    if (!isTeamTask && !isPersonalTask) {
      throw new Error(`Invalid task ID format: ${prefixedTaskId}`);
    }

    const realTaskId = isTeamTask
      ? prefixedTaskId.replace('team-', '')
      : prefixedTaskId.replace('personal-', '');

    // Get the correct document reference
    const taskRef = isTeamTask
      ? doc(db, 'teams', teamId, 'tasks', realTaskId)
      : doc(db, 'users', userId, 'tasks', realTaskId);

    await updateDoc(taskRef, {
      status: 'dismissed',
      updatedAt: serverTimestamp(),
    });

    logger.info('[TasksService] Archived dashboard task:', {
      prefixedTaskId,
      realTaskId,
      isTeamTask,
    });
  } catch (error) {
    logger.error('[TasksService] Failed to archive dashboard task:', error);
    throw error;
  }
}

/**
 * Reschedule a task to a new due date
 * Routes to correct Firestore path based on task scope
 */
export async function rescheduleTask(
  userId: string,
  taskId: string,
  dueAt: Date,
  teamId?: string
): Promise<Task> {
  try {
    const task = await getTask(userId, taskId, teamId);
    if (!task) {
      throw new Error('Task not found');
    }

    // Route based on scope
    const isTeamTask = task.scope === 'team';
    const taskRef =
      isTeamTask && teamId
        ? doc(db, 'teams', teamId, 'tasks', taskId)
        : doc(db, 'users', userId, 'tasks', taskId);

    await updateDoc(taskRef, {
      dueAt: Timestamp.fromDate(dueAt),
      updatedAt: serverTimestamp(),
    });

    const updatedTask = await getTask(userId, taskId, teamId);
    if (!updatedTask) {
      throw new Error('Failed to retrieve updated task');
    }

    return updatedTask;
  } catch (error) {
    logger.error('[TasksService] Failed to reschedule task:', error);
    throw error;
  }
}

/**
 * Soft delete a task
 * Routes to correct Firestore path based on task scope
 */
export async function deleteTask(userId: string, taskId: string, teamId?: string): Promise<void> {
  try {
    const task = await getTask(userId, taskId, teamId);
    if (!task) {
      throw new Error('Task not found');
    }

    // Route based on scope
    const isTeamTask = task.scope === 'team';
    const taskRef =
      isTeamTask && teamId
        ? doc(db, 'teams', teamId, 'tasks', taskId)
        : doc(db, 'users', userId, 'tasks', taskId);

    await updateDoc(taskRef, {
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    logger.error('[TasksService] Failed to delete task:', error);
    throw error;
  }
}

/**
 * Restore a soft-deleted task
 * Routes to correct Firestore path based on task scope
 */
export async function restoreTask(userId: string, taskId: string, teamId?: string): Promise<Task> {
  try {
    // For restore, we need to check both paths since the task might be soft-deleted
    // Try personal first
    let taskRef = doc(db, 'users', userId, 'tasks', taskId);
    let taskSnap = await getDoc(taskRef);

    if (!taskSnap.exists() && teamId) {
      // Try team tasks
      taskRef = doc(db, 'teams', teamId, 'tasks', taskId);
      taskSnap = await getDoc(taskRef);
    }

    if (!taskSnap.exists()) {
      throw new Error('Task not found');
    }

    await updateDoc(taskRef, {
      deletedAt: null,
      updatedAt: serverTimestamp(),
    });

    const updatedTask = await getTask(userId, taskId, teamId);
    if (!updatedTask) {
      throw new Error('Task not found after restore');
    }
    return updatedTask;
  } catch (error) {
    logger.error('[TasksService] Failed to restore task:', error);
    throw error;
  }
}

/**
 * Permanently delete a task (hard delete)
 * Routes to correct Firestore path based on task scope
 */
export async function permanentlyDeleteTask(
  userId: string,
  taskId: string,
  teamId?: string
): Promise<void> {
  try {
    // Try personal first
    let taskRef = doc(db, 'users', userId, 'tasks', taskId);
    let taskSnap = await getDoc(taskRef);

    if (!taskSnap.exists() && teamId) {
      // Try team tasks
      taskRef = doc(db, 'teams', teamId, 'tasks', taskId);
      taskSnap = await getDoc(taskRef);
    }

    if (!taskSnap.exists()) {
      throw new Error('Task not found');
    }

    await deleteDoc(taskRef);
  } catch (error) {
    logger.error('[TasksService] Failed to permanently delete task:', error);
    throw error;
  }
}

/**
 * Bulk update task statuses
 */
export async function bulkUpdateTaskStatus(
  userId: string,
  taskIds: string[],
  status: TaskStatus
): Promise<void> {
  try {
    const batch = writeBatch(db);

    for (const taskId of taskIds) {
      const taskRef = doc(db, 'users', userId, 'tasks', taskId);
      const updates: Record<string, unknown> = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (status === 'done') {
        updates.completedAt = serverTimestamp();
      } else {
        updates.completedAt = null;
      }

      batch.update(taskRef, updates);
    }

    await batch.commit();
  } catch (error) {
    logger.error('[TasksService] Failed to bulk update tasks:', error);
    throw error;
  }
}

/**
 * Bulk delete tasks (soft delete)
 */
export async function bulkDeleteTasks(userId: string, taskIds: string[]): Promise<void> {
  try {
    const batch = writeBatch(db);

    for (const taskId of taskIds) {
      const taskRef = doc(db, 'users', userId, 'tasks', taskId);
      batch.update(taskRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    await batch.commit();
  } catch (error) {
    logger.error('[TasksService] Failed to bulk delete tasks:', error);
    throw error;
  }
}
